// Shared chrome for the QC modals, from the prototype's shells — divided header, 22/24px body, divided footer.

import { styled } from '@mui/material/styles';
import { ACCENT_BLUE, ACCENT_BLUE_DARK, FIRST_PASS_PURPLE } from './qcTheme';

export const ModalHeader = styled('div')({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: '16px',
  padding: '22px 24px 16px',
  borderBottom: '1px solid rgba(0,0,0,0.1)',
});

export const ModalTitle = styled('h2')({
  fontSize: '1.15rem',
  fontWeight: 500,
  color: 'rgba(0,0,0,0.87)',
  margin: 0,
});

export const ModalLede = styled('p')({
  fontSize: '0.875rem',
  lineHeight: 1.5,
  color: 'rgba(0,0,0,0.6)',
  margin: '4px 0 0',
});

export const CloseButton = styled('button')({
  border: 'none',
  background: 'none',
  padding: 0,
  display: 'inline-flex',
  flex: 'none',
  cursor: 'pointer',
  color: 'rgba(0,0,0,0.55)',
  '&:hover': {
    color: 'rgba(0,0,0,0.8)',
  },
});

export const ModalBody = styled('div')({
  padding: '22px 24px',
});

export const ModalFooter = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '12px',
  padding: '16px 24px 22px',
  borderTop: '1px solid rgba(0,0,0,0.1)',
});

export const FooterActions = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  marginLeft: 'auto',
});

export const FooterNote = styled('span')({
  fontSize: '0.8125rem',
  color: 'rgba(0,0,0,0.6)',
});

const buttonBase = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '7px',
  borderRadius: '4px',
  fontSize: '0.8125rem',
  fontWeight: 600,
  letterSpacing: '0.46px',
  textTransform: 'uppercase' as const,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

export const TextButton = styled('button')({
  ...buttonBase,
  background: '#fff',
  color: 'rgba(0,0,0,0.7)',
  border: 'none',
  padding: '9px 14px',
  '&:hover': {
    background: 'rgba(0,0,0,0.04)',
  },
});

export const LinkButton = styled('button')({
  ...buttonBase,
  background: '#fff',
  color: ACCENT_BLUE,
  border: 'none',
  padding: '9px 14px',
  '&:hover': {
    background: 'rgba(25,118,210,0.04)',
  },
});

export const OutlinedButton = styled('button')({
  ...buttonBase,
  background: '#fff',
  color: ACCENT_BLUE,
  border: '1px solid rgba(25,118,210,0.5)',
  padding: '9px 18px',
  '&:hover': {
    background: 'rgba(25,118,210,0.04)',
    borderColor: ACCENT_BLUE,
  },
  '&:disabled': {
    color: 'rgba(0,0,0,0.4)',
    borderColor: 'rgba(0,0,0,0.2)',
    background: '#fff',
    cursor: 'not-allowed',
  },
});

export const PrimaryButton = styled('button')({
  ...buttonBase,
  background: ACCENT_BLUE,
  color: '#fff',
  border: 'none',
  padding: '10px 22px',
  boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
  '&:hover': {
    background: ACCENT_BLUE_DARK,
  },
  '&:disabled': {
    background: 'rgba(0,0,0,0.12)',
    color: 'rgba(0,0,0,0.4)',
    boxShadow: 'none',
    cursor: 'not-allowed',
  },
});

// First Pass surfaces carry the purple accent rather than action blue.
export const FirstPassButton = styled('button')({
  ...buttonBase,
  background: FIRST_PASS_PURPLE,
  color: '#fff',
  border: 'none',
  padding: '10px 20px',
  boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
  '&:hover': {
    background: '#4e2c96',
  },
});

export const SectionCaption = styled('span')({
  fontSize: '0.7rem',
  letterSpacing: '1px',
  fontWeight: 600,
  color: 'rgba(0,0,0,0.6)',
  textTransform: 'uppercase',
});

export const CategoryChip = styled('span', {
  shouldForwardProp: prop => prop !== 'rgb',
})<{ rgb: string }>(({ rgb }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '7px',
  height: '24px',
  padding: '0 11px',
  borderRadius: '12px',
  fontSize: '0.72rem',
  fontWeight: 600,
  letterSpacing: '0.2px',
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  justifySelf: 'start',
  background: `rgba(${rgb},0.08)`,
  color: `rgb(${rgb})`,
  border: `1px solid rgba(${rgb},0.24)`,
  '&::before': {
    content: '""',
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    background: `rgb(${rgb})`,
    flex: 'none',
  },
}));

export const AuthorAvatar = styled('span', {
  shouldForwardProp: prop => prop !== 'size' && prop !== 'isAdmin',
})<{ size: number; isAdmin: boolean }>(({ size, isAdmin }) => ({
  width: `${size}px`,
  height: `${size}px`,
  borderRadius: '50%',
  flex: 'none',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: size >= 40 ? '0.85rem' : '0.72rem',
  fontWeight: 700,
  background: isAdmin ? 'rgba(25,118,210,0.14)' : 'rgba(0,109,119,0.14)',
  color: isAdmin ? ACCENT_BLUE_DARK : '#006d77',
}));

export const QuestionCode = styled('span')({
  fontSize: '0.8rem',
  fontWeight: 700,
  color: ACCENT_BLUE_DARK,
  whiteSpace: 'nowrap',
});
